import { useCallback, useMemo, useState } from "react";
import { Form, Field } from "react-final-form";
import { useQueryClient } from "@tanstack/react-query";
import Icon from "components/Icon";
import { AdminCreatableSelectAdapter, AdminSelectAdapter } from "components/select";
import clubPlaceholder from "assets/images/logo_fixtures.png";
import client from "libs/client";
import { getApiErrorMessage } from "libs/utils";
import type {
	AgeCategoryEnum,
	CompetitionRowResponse,
	CompetitionTier,
	CompetitionType,
	CompetitionUpsertRequest,
	GenderEnum,
} from "libs/apiClient";
import { getBase64 } from "pages/productManagement/functions";
import { useToast } from "ui-kit";
import { useDropzone } from "react-dropzone";
import {
	COMPETITION_AGE_GROUP_OPTIONS,
	COMPETITION_FORMAT_OPTIONS,
	COMPETITION_GENDER_OPTIONS,
	COMPETITION_MATCH_LENGTH_OPTIONS,
	COMPETITION_TIER_OPTIONS,
	COMPETITION_TYPE_OPTIONS,
	INTERNATIONAL_COUNTRY_CODE,
	isDomesticCompetitionType,
	resolveCompetitionLogoUrl,
	type SelectOption,
} from "./competitionOptions";

type CompetitionFormValues = {
	name: string;
	type: SelectOption<CompetitionType> | null;
	country: SelectOption | null;
	ageGroup: SelectOption<AgeCategoryEnum> | null;
	gender: SelectOption<GenderEnum> | null;
	tier: SelectOption<CompetitionTier> | null;
	region: SelectOption | null;
	competitionLevel: string;
	hasPhases: boolean;
	hasGroups: boolean;
	format: SelectOption | null;
	matchLengthMinutes: SelectOption | null;
	isScoutVisible: boolean;
};

const COMPETITION_LEVEL_MIN = 1;
const COMPETITION_LEVEL_MAX = 1000;
const COMPETITION_LEVEL_ERROR = "Competition level must be a whole number between 1 and 1000";

type CompetitionFormErrors = {
	name?: string;
	country?: string;
	ageGroup?: string;
	gender?: string;
	logoUrl?: string;
	type?: string;
	competitionLevel?: string;
};

function parseCompetitionLevel(raw: string): { value: number | null; error?: string } {
	const trimmed = raw.trim();
	if (trimmed === "") {
		return { value: null };
	}

	if (!/^\d+$/.test(trimmed)) {
		return { value: null, error: COMPETITION_LEVEL_ERROR };
	}

	const level = Number(trimmed);
	if (!Number.isSafeInteger(level) || level < COMPETITION_LEVEL_MIN || level > COMPETITION_LEVEL_MAX) {
		return { value: null, error: COMPETITION_LEVEL_ERROR };
	}

	return { value: level };
}

export type CompetitionFormModalProps = {
	competition?: CompetitionRowResponse | null;
	onClose: () => void;
	onSaved: () => void;
};

function extractMutationErrorMessage(error: unknown): string {
	if (error && typeof error === "object") {
		const err = error as {
			json?: string | { message?: string };
			message?: string;
		};
		if (typeof err.json === "string") {
			return err.json;
		}
		if (err.json?.message) {
			return err.json.message;
		}
		if (err.message) {
			return err.message;
		}
	}
	return "Something went wrong";
}

function findOption<T extends string>(options: SelectOption<T>[], value: string | null | undefined): SelectOption<T> | null {
	if (value == null || value === "") {
		return null;
	}
	return options.find((option) => option.value === value) ?? { value: value as T, label: value };
}

function validateCompetitionForm(values: CompetitionFormValues, logoUrl: string): CompetitionFormErrors {
	const errors: CompetitionFormErrors = {};
	const trimmedName = (values.name ?? "").trim();

	if (trimmedName === "") {
		errors.name = "Name is required";
	}
	if (values.type == null) {
		errors.type = "Type is required";
	}
	if (values.country == null || values.country.value === "") {
		errors.country = "Country is required";
	}
	if (values.ageGroup == null) {
		errors.ageGroup = "Age group is required";
	}
	if (values.gender == null) {
		errors.gender = "Gender is required";
	}
	if (logoUrl.trim() === "") {
		errors.logoUrl = "Logo is required";
	}
	if (isDomesticCompetitionType(values.type?.value)) {
		const competitionLevel = parseCompetitionLevel(values.competitionLevel ?? "");
		if (competitionLevel.error != null) {
			errors.competitionLevel = competitionLevel.error;
		}
	}

	return errors;
}

export function CompetitionFormModal({ competition, onClose, onSaved }: CompetitionFormModalProps) {
	const queryClient = useQueryClient();
	const { addSuccessToast, addErrorToast } = useToast();
	const isEdit = competition?.id != null;
	const [logoUrl, setLogoUrl] = useState(competition?.logoUrl ?? "");
	const [isUploadingLogo, setIsUploadingLogo] = useState(false);

	const { data: continentsResponse } = client.getContinentCountry.useQuery({ retry: false });
	const { data: regionsResponse } = client.getCompetitionRegions.useQuery({ retry: false });

	const createMutation = client.postCompetition.useMutation();
	const updateMutation = client.putCompetition.useMutation();
	const uploadLogoMutation = client.uploadCompetitionLogo.useMutation();

	const countryOptions = useMemo((): SelectOption[] => {
		const continents = continentsResponse?.data ?? [];
		const options: SelectOption[] = [{ value: INTERNATIONAL_COUNTRY_CODE, label: "International" }];

		for (const continent of continents) {
			for (const country of continent.options ?? []) {
				options.push({ value: country.value, label: country.label });
			}
		}

		return options;
	}, [continentsResponse]);

	const regionOptions = useMemo((): SelectOption[] => {
		const regions = regionsResponse?.data?.items ?? [];
		return regions.map((region) => ({ value: region, label: region }));
	}, [regionsResponse]);

	const initialValues = useMemo((): CompetitionFormValues => {
		return {
			name: competition?.name ?? "",
			type: findOption(COMPETITION_TYPE_OPTIONS, competition?.type),
			country: findOption(countryOptions, competition?.country),
			ageGroup: findOption(COMPETITION_AGE_GROUP_OPTIONS, competition?.ageGroup),
			gender: findOption(COMPETITION_GENDER_OPTIONS, competition?.gender),
			tier: findOption(COMPETITION_TIER_OPTIONS, competition?.tier),
			region:
				competition?.region != null && competition.region !== ""
					? { value: competition.region, label: competition.region }
					: null,
			competitionLevel:
				competition?.competitionLevel != null ? String(competition.competitionLevel) : "",
			hasPhases: competition?.hasPhases === true,
			hasGroups: competition?.hasGroups === true,
			format: findOption(COMPETITION_FORMAT_OPTIONS, competition?.format),
			matchLengthMinutes:
				competition?.matchLengthMinutes != null
					? findOption(COMPETITION_MATCH_LENGTH_OPTIONS, String(competition.matchLengthMinutes))
					: null,
			isScoutVisible: competition?.isScoutVisible !== false,
		};
	}, [competition, countryOptions]);

	const logoPreviewSrc = useMemo(() => {
		const resolvedLogoUrl = resolveCompetitionLogoUrl(logoUrl);
		if (resolvedLogoUrl === "") {
			return clubPlaceholder;
		}

		return resolvedLogoUrl;
	}, [logoUrl]);

	const onDrop = useCallback(
		async (acceptedFiles: File[]) => {
			const file = acceptedFiles[0];
			if (file == null) {
				return;
			}

			setIsUploadingLogo(true);
			try {
				const base64 = (await getBase64(file)) as string;
				const response = await uploadLogoMutation.mutateAsync({ body: { file: base64 } });
				const uploadedUrl = response?.data?.url;
				if (uploadedUrl == null || uploadedUrl === "") {
					addErrorToast("Logo upload failed");
					return;
				}
				setLogoUrl(uploadedUrl);
				addSuccessToast("Logo uploaded");
			} catch (error: unknown) {
				addErrorToast(getApiErrorMessage(extractMutationErrorMessage(error)));
			} finally {
				setIsUploadingLogo(false);
			}
		},
		[addErrorToast, addSuccessToast, uploadLogoMutation],
	);

	const { getInputProps, getRootProps, open: openFileDialog } = useDropzone({
		onDrop,
		accept: { "image/jpeg": [".jpg", ".jpeg"], "image/png": [".png"] },
		multiple: false,
		maxFiles: 1,
		noClick: true,
		noKeyboard: true,
	});

	const handleSubmit = async (values: CompetitionFormValues) => {
		const type = values.type?.value;
		if (type == null || values.country == null || values.ageGroup == null || values.gender == null) {
			return;
		}

		const domestic = isDomesticCompetitionType(type);
		const parsedCompetitionLevel = parseCompetitionLevel(values.competitionLevel);
		if (domestic && parsedCompetitionLevel.error != null) {
			return { competitionLevel: parsedCompetitionLevel.error };
		}

		const matchLengthRaw = values.matchLengthMinutes?.value;
		const matchLengthMinutes =
			matchLengthRaw != null && matchLengthRaw !== "" && Number.isFinite(Number(matchLengthRaw))
				? Number(matchLengthRaw)
				: null;

		const body: CompetitionUpsertRequest = {
			name: values.name.trim(),
			type,
			tier: domestic ? (values.tier?.value ?? null) : null,
			hasPhases: values.hasPhases === true,
			hasGroups: values.hasGroups === true,
			competitionLevel: domestic ? parsedCompetitionLevel.value : null,
			country: values.country.value,
			region:
				values.region?.value != null && String(values.region.value).trim() !== ""
					? String(values.region.value).trim()
					: null,
			ageGroup: values.ageGroup.value,
			gender: values.gender.value,
			matchLengthMinutes,
			format: values.format?.value ?? null,
			logoUrl,
			isScoutVisible: values.isScoutVisible === true,
		};

		try {
			if (isEdit && competition?.id != null) {
				await updateMutation.mutateAsync({ id: competition.id, body });
				addSuccessToast("Competition updated");
			} else {
				await createMutation.mutateAsync({ body });
				addSuccessToast("Competition created");
			}

			await queryClient.invalidateQueries({ queryKey: ["getCompetitions"] });
			await queryClient.invalidateQueries({ queryKey: ["getCompetitionRegions"] });
			onSaved();
			onClose();
		} catch (error: unknown) {
			addErrorToast(getApiErrorMessage(extractMutationErrorMessage(error)));
		}
	};

	const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

	return (
		<div className="modal open">
			<span className="modal__backdrop" onClick={onClose} />
			<div className="modal__container modal__container--lg">
				<span className="modal__close" onClick={onClose}>
					<Icon icon="cross" />
				</span>
				<div className="modal__title">{isEdit ? "Edit competition" : "New competition"}</div>
				<div className="modal__body">
					<div className="admin-creation-container">
						<div className="admin-creation-form">
							<div className="user-row">
								<div className="admin-user-pic" {...getRootProps()}>
									<img src={logoPreviewSrc} alt="Competition logo" />
									<label
										className="tableCellControls__button tableCellControls__button--svg"
										onClick={(event) => {
											event.preventDefault();
											openFileDialog();
										}}
									>
										<Icon icon="crayon" color="white" />
										<input
											id="competition_logoFile"
											name="competition[logoFile]"
											className="js-input-file js-disable-tab custom-file-input"
											{...getInputProps()}
										/>
									</label>
								</div>
								{isUploadingLogo ? <span className="form-hint">Uploading…</span> : null}
							</div>

							<Form
								onSubmit={handleSubmit}
								initialValues={initialValues}
								validate={(values) => validateCompetitionForm(values, logoUrl)}
								keepDirtyOnReinitialize
								render={({ handleSubmit: submitForm, values, errors, touched, submitFailed }) => {
									const showDomesticFields = isDomesticCompetitionType(values.type?.value);
									const showFieldError = (field: keyof CompetitionFormErrors) =>
										Boolean((touched?.[field] || submitFailed) && errors?.[field]);

									return (
										<form onSubmit={submitForm} noValidate>
											{showFieldError("logoUrl") ? (
												<span className="form-error">{errors?.logoUrl}</span>
											) : null}

											<div className="form-row">
												<div className="field-group">
													<label className="required">Name</label>
													<Field name="name">
														{({ input }) => (
															<input {...input} className="form-control" maxLength={255} />
														)}
													</Field>
													{showFieldError("name") ? (
														<span className="form-error">{errors?.name}</span>
													) : null}
												</div>

												<div className="field-group">
													<label className="required">Type</label>
													<Field name="type" component={AdminSelectAdapter} options={COMPETITION_TYPE_OPTIONS} />
													{showFieldError("type") ? <span className="form-error">{errors?.type}</span> : null}
												</div>

												<div className="field-group">
													<label className="required">Country</label>
													<Field name="country" component={AdminSelectAdapter} options={countryOptions} />
													{showFieldError("country") ? (
														<span className="form-error">{errors?.country}</span>
													) : null}
												</div>

												<div className="field-group">
													<label className="required">Age group</label>
													<Field
														name="ageGroup"
														component={AdminSelectAdapter}
														options={COMPETITION_AGE_GROUP_OPTIONS}
													/>
													{showFieldError("ageGroup") ? (
														<span className="form-error">{errors?.ageGroup}</span>
													) : null}
												</div>

												<div className="field-group">
													<label className="required">Gender</label>
													<Field
														name="gender"
														component={AdminSelectAdapter}
														options={COMPETITION_GENDER_OPTIONS}
													/>
													{showFieldError("gender") ? (
														<span className="form-error">{errors?.gender}</span>
													) : null}
												</div>

												{showDomesticFields ? (
													<>
														<div className="field-group">
															<label>Tier</label>
															<Field
																name="tier"
																component={AdminSelectAdapter}
																options={COMPETITION_TIER_OPTIONS}
																isClearable
															/>
														</div>
														<div className="field-group">
															<label>Competition level</label>
															<Field name="competitionLevel">
																{({ input }) => (
																	<input
																		{...input}
																		type="number"
																		min={COMPETITION_LEVEL_MIN}
																		max={COMPETITION_LEVEL_MAX}
																		step={1}
																		className="form-control"
																	/>
																)}
															</Field>
															{showFieldError("competitionLevel") ? (
																<span className="form-error">{errors?.competitionLevel}</span>
															) : null}
														</div>
													</>
												) : null}

												<div className="field-group">
													<label>Region</label>
													<Field
														name="region"
														component={AdminCreatableSelectAdapter}
														options={regionOptions}
														isClearable
														isSearchable
														placeholder="Select or create…"
													/>
												</div>

												<div className="field-group">
													<label>Format</label>
													<Field
														name="format"
														component={AdminSelectAdapter}
														options={COMPETITION_FORMAT_OPTIONS}
														isClearable
													/>
												</div>

												<div className="field-group">
													<label>Match length (minutes)</label>
													<Field
														name="matchLengthMinutes"
														component={AdminSelectAdapter}
														options={COMPETITION_MATCH_LENGTH_OPTIONS}
														isClearable
													/>
												</div>
											</div>

											<div className="form-row competitions-form-toggles">
												<label className="competitions-checkbox">
													<Field name="hasPhases" type="checkbox" component="input" />
													<span>Has phases</span>
												</label>
												<label className="competitions-checkbox">
													<Field name="hasGroups" type="checkbox" component="input" />
													<span>Has groups</span>
												</label>
												<label className="competitions-checkbox">
													<Field name="isScoutVisible" type="checkbox" component="input" />
													<span>Scout visible</span>
												</label>
											</div>

											<div className="form-row competitions-form-actions">
												<button type="button" className="btn btn--cancel" onClick={onClose}>
													Cancel
												</button>
												<button type="submit" className="btn btn-light-blue" disabled={isSubmitting || isUploadingLogo}>
													{isEdit ? "Save changes" : "Create competition"}
												</button>
											</div>
										</form>
									);
								}}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default CompetitionFormModal;
